import { ConversationMessage, MediaMessage } from '@partoohub/api-client';
import { compareDesc } from 'date-fns';
import { InfiniteData, QueryClient } from 'react-query';

import { getMessagesKey } from '../hooks/useGetMessages';

const isSameMessage = (a: ConversationMessage, b: ConversationMessage) => {
    if (a.id === b.id) return true;
    if (a.date === b.date && a.content === b.content && a.content_type === b.content_type)
        return true;

    if (a.content_type !== b.content_type) return false;

    if (a.content_type === 'Video' || a.content_type === 'Audio' || a.content_type === 'Document') {
        return a.date === b.date && a.content.url === (b as MediaMessage).content.url;
    }

    return false;
};

export const addNewMessage = (
    queryClient: QueryClient,
    conversationId: number,
    newMessage: ConversationMessage,
) => {
    const queryDataKey = getMessagesKey(conversationId);
    const messagePages: InfiniteData<Record<string, any>> | undefined =
        queryClient.getQueryData(queryDataKey);

    if (!messagePages) return;

    const firstPageMessages = messagePages.pages[0]?.['messages'] || [];

    firstPageMessages.push(newMessage);
    firstPageMessages.sort(datePredicate);
    queryClient.setQueryData(queryDataKey, messagePages);
};

export const updateMessage = (
    queryClient: QueryClient,
    conversationId: number,
    updatedMessage: ConversationMessage,
) => {
    const queryDataKey = getMessagesKey(conversationId);
    const messagePages: InfiniteData<Record<string, any>> | undefined =
        queryClient.getQueryData(queryDataKey);

    if (!messagePages) return;

    messagePages.pages.forEach(page => {
        const updatedMessages = page['messages'].map((message: ConversationMessage) => {
            if (isSameMessage(message, updatedMessage)) {
                return updatedMessage;
            }

            return message;
        });

        page['messages'] = updatedMessages;
    });

    queryClient.setQueryData(queryDataKey, messagePages);
};

export const moveMessageToHead = (
    queryClient: QueryClient,
    conversationId: number,
    retryMessage: ConversationMessage,
) => {
    const queryDataKey = getMessagesKey(conversationId);
    const messagePages: InfiniteData<Record<string, any>> | undefined =
        queryClient.getQueryData(queryDataKey);

    if (!messagePages) return;

    messagePages.pages.forEach(page => {
        const retryMessageIndex = page['messages'].findIndex((message: ConversationMessage) =>
            retryMessage.id ? message.id === retryMessage.id : message.date === retryMessage.date,
        );
        if (retryMessageIndex !== -1) {
            page['messages'].splice(retryMessageIndex, 1);
        }
    });
    const firstPageMessages = messagePages.pages[0]?.['messages'] || [];

    firstPageMessages.push(retryMessage);
    firstPageMessages.sort(datePredicate);
    queryClient.setQueryData(queryDataKey, messagePages);
};

export const doesMessageExists = (
    queryClient: QueryClient,
    conversationId: number,
    checkedMessage: ConversationMessage,
) => {
    const queryDataKey = getMessagesKey(conversationId);
    const messagePages: InfiniteData<Record<string, any>> | undefined =
        queryClient.getQueryData(queryDataKey);

    if (!messagePages) return false;

    return messagePages.pages.some(page => {
        return page['messages'].some((message: ConversationMessage) =>
            isSameMessage(message, checkedMessage),
        );
    });
};

const datePredicate = (a: ConversationMessage, b: ConversationMessage) => {
    return compareDesc(new Date(a.date), new Date(b.date));
};
